// JOBE-0167.1: PromptOpsMemoryPage — W8 root.
//
// Replaces the JOBE-0159.4 inline stub at /promptops/memory with the
// real tab scaffold. Subsequent JOBE-0167 subtasks (.2 list+filter,
// .3 inspect view, .4 actions, .5 forget log / decay / health) attach
// their content into the four tab body stubs declared here.
//
// Reads URL state via ?tab=memories|forget-log|decay|health (default
// memories; unknown values fall back to memories). Gated on the
// memory.governance.route feature flag.

import { useSearchParams } from 'react-router-dom';
import { useFlagStore } from '../lib/useFlagStore.js';
import { PromptOpsSubNav } from '../components/PromptOpsSubNav/PromptOpsSubNav.jsx';
import { MemoryTabBar, TAB_KEYS } from './memory/MemoryTabBar.jsx';
import { MemoriesTabBody } from './memory/MemoriesTabBody.jsx';
import { ForgetLogTabBody } from './memory/ForgetLogTabBody.jsx';
import { DecayTabBody } from './memory/DecayTabBody.jsx';
import { HealthTabBody } from './memory/HealthTabBody.jsx';
import './PromptOpsMemoryPage.css';
import './memory/MemoriesTabBody.css';
import './memory/secondaryTabs.css';

const VALID_TABS = new Set(TAB_KEYS);
const DEFAULT_TAB = 'memories';

export function PromptOpsMemoryPage() {
  const isFlagOn = useFlagStore((s) => s.isFlagOn);
  const routeEnabled = isFlagOn('memory.governance.route');
  const [params, setParams] = useSearchParams();

  const rawTab = params.get('tab');
  const activeTab = VALID_TABS.has(rawTab) ? rawTab : DEFAULT_TAB;

  function setTab(next) {
    setParams((prev) => {
      const out = new URLSearchParams(prev);
      out.set('tab', next);
      return out;
    });
  }

  if (!routeEnabled) {
    return (
      <>
        <PromptOpsSubNav agentName={null} />
        <div
          className="page-stub"
          data-testid="promptops-memory-disabled"
        >
          <h1>PromptOps — Memory</h1>
          <p>
            This surface is disabled. Enable the
            <code> memory.governance.route </code>
            feature flag to access it.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PromptOpsSubNav agentName={null} />
      <div className="promptops-memory-page" data-testid="promptops-memory-page">
        <header className="promptops-memory-page__header">
          <nav className="promptops-memory-page__breadcrumb" aria-label="breadcrumb">
            <span>PromptOps</span>
            <span aria-hidden="true"> / </span>
            <span aria-current="page">Memory</span>
          </nav>
          <h1 className="promptops-memory-page__title">Memory</h1>
        </header>
        <MemoryTabBar activeTab={activeTab} onTab={setTab} />
        <section className="promptops-memory-page__body">
          {activeTab === 'memories' && (
            <div data-testid="memories-tab-stub">
              <MemoriesTabBody />
            </div>
          )}
          {activeTab === 'forget-log' && (
            <div data-testid="forget-log-tab-stub">
              <ForgetLogTabBody />
            </div>
          )}
          {activeTab === 'decay' && (
            <div data-testid="decay-tab-stub">
              <DecayTabBody />
            </div>
          )}
          {activeTab === 'health' && (
            <div data-testid="health-tab-stub">
              <HealthTabBody />
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export default PromptOpsMemoryPage;
